/**
 * Firebase Authentication Utilities for raisy_FE
 * Adapted from glowtest_FE authUtils.js pattern
 *
 * Ensures authentication token always exists before making API calls.
 * Pattern: Check cookie → Check Firebase user → Throw error if not authenticated
 *
 * D-006: auth state lives in JS-readable cookies (SameSite=Lax, Path=/, 30d),
 * not localStorage. See utils/authCookies.ts.
 */

import { auth } from "@/config/firebase";
import {
  AUTH_TOKEN_COOKIE,
  AUTH_USER_COOKIE,
  setAuthCookie,
  getAuthCookie,
  getAuthJsonCookie,
  removeAuthCookie,
} from "@/utils/authCookies";

/**
 * Global authentication utilities to manage token state
 */
class AuthUtils {
  // Track if token refresh is currently in progress
  private static isRefreshingToken = false;

  // Store the promise of ongoing token refresh to avoid duplicate calls
  private static ongoingTokenRefresh: Promise<string> | null = null;

  /**
   * Ensures a valid authentication token exists
   * Pattern adapted from glowtest_FE: checks cookie, then Firebase user, then throws
   *
   * @returns {Promise<string>} The authentication token
   * @throws {Error} If not authenticated and cannot obtain token
   */
  static async ensureAuthToken(): Promise<string> {
    // If already refreshing, wait for the ongoing process
    if (this.isRefreshingToken && this.ongoingTokenRefresh) {
      console.log("🔐 [AUTH_UTILS] Token refresh already in progress, waiting...");
      try {
        return await this.ongoingTokenRefresh;
      } catch (error) {
        console.error("🔐 [AUTH_UTILS] Error waiting for ongoing token refresh:", error);
        throw error;
      }
    }

    // Always get a fresh token from Firebase — getIdToken(false) is cheap
    // (returns cached token if still valid, refreshes only when expired)
    const user = auth.currentUser;
    if (user) {
      this.isRefreshingToken = true;
      try {
        this.ongoingTokenRefresh = user.getIdToken(); // auto-refreshes if expired
        const token = await this.ongoingTokenRefresh;
        setAuthCookie(AUTH_TOKEN_COOKIE, token);
        return token;
      } catch (error) {
        console.error("🔐 [AUTH_UTILS] Failed to refresh token:", error);
        // Fall through to cookie fallback
      } finally {
        this.isRefreshingToken = false;
        this.ongoingTokenRefresh = null;
      }
    }

    // Fallback: use the stored cookie token (e.g., during initial load before Firebase resolves)
    const currentToken = getAuthCookie(AUTH_TOKEN_COOKIE);
    if (currentToken && currentToken.trim()) {
      console.log("🔐 [AUTH_UTILS] Using cookie fallback token");
      return currentToken;
    }

    throw new Error("Not authenticated: no Firebase user and no stored token");
  }

  /**
   * Get the current token without triggering authentication
   * @returns {string|null} The current token or null if not available
   */
  static getCurrentToken(): string | null {
    const token = getAuthCookie(AUTH_TOKEN_COOKIE);
    return token && token.trim() ? token : null;
  }

  /**
   * Check if a valid token exists
   * @returns {boolean} True if a valid token exists
   */
  static hasValidToken(): boolean {
    const token = this.getCurrentToken();
    return !!token;
  }

  /**
   * Clear authentication state (for logout)
   */
  static clearAuthState(): void {
    this.isRefreshingToken = false;
    this.ongoingTokenRefresh = null;
    // Note: Don't clear localStorage here as that's handled by Firebase logout
  }

  /**
   * Get headers with authentication for API calls
   * Ensures token exists before constructing headers
   *
   * @param {string} [token] - Optional token to use, will call ensureAuthToken if not provided
   * @returns {Promise<Record<string, string>>} Headers object with Authorization
   */
  static async getAuthHeaders(token?: string): Promise<Record<string, string>> {
    const authToken = token || await this.ensureAuthToken();
    return {
      Authorization: `Bearer ${authToken}`,
      "Content-Type": "application/json",
    };
  }

  /**
   * Get stored user info
   * @returns {Record<string, any>|null} Stored user object or null
   */
  static getStoredUser(): Record<string, any> | null {
    return getAuthJsonCookie<Record<string, any>>(AUTH_USER_COOKIE);
  }

  /**
   * Clear stored user info (used when the user's own entity is deleted)
   */
  static clearStoredUser(): void {
    removeAuthCookie(AUTH_USER_COOKIE);
    removeAuthCookie(AUTH_TOKEN_COOKIE);
  }
}

export default AuthUtils;
